/* eslint-disable react/no-unescaped-entities */
import { Compass, Factory, Handshake, Globe2, TrendingUp, ShieldCheck } from 'lucide-react';
import { PageHero } from '../components/SectionHeading';
import { ScrollReveal, Stagger, StaggerItem } from '../components/Motion';
import { ASSETS } from '../data/content';
import { useLang } from '../i18n/LanguageContext';

const CRITERIA_EN = [
    ['Geological quality', 'Proven petroleum system with certified prospective resources and Direct Hydrocarbon Indicators'],
    ['Basin context', 'Established or emerging basin with demonstrated exploration success rate'],
    ['Infrastructure proximity', 'Access to existing or planned export infrastructure'],
    ['Fiscal terms', 'Favorable Production Sharing Agreement or EPSA structure'],
    ['Entry timing', 'Early-mover positioning prior to major capital deployment by larger operators'],
    ['Return profile', 'Minimum projected IRR of 20–25% at base-case commodity prices'],
];
const CRITERIA_AR = [
    ['الجودة الجيولوجية', 'نظام بترولي مثبت مع موارد محتملة معتمدة ومؤشرات هيدروكربون مباشرة'],
    ['سياق الحوض', 'حوض ثابت أو ناشئ بمعدل نجاح استكشاف مثبت'],
    ['قرب البنية التحتية', 'الوصول إلى بنية تحتية تصديرية قائمة أو مخططة'],
    ['الشروط المالية', 'اتفاقية تقاسم إنتاج مواتية أو هيكل EPSA'],
    ['توقيت الدخول', 'تموضع مبكر قبل ضخ رأس المال الكبير من قبل المشغلين الأكبر'],
    ['ملف العائد', 'حد أدنى لمعدل العائد الداخلي 20–25٪ عند أسعار السلع الأساسية'],
];

const GAS_BRANCHES_EN = [
    { label: 'Domestic Gas Sales', sub: 'Stable regulated revenue' },
    { label: 'Gas-to-Power CCGT', sub: 'Long-term power offtake' },
    { label: 'Pipeline Transit Tariffs', sub: 'Infrastructure income' },
    { label: 'Industrial & Fertilizer', sub: 'Anchor domestic demand' },
    { label: 'LNG Export', sub: 'International pricing uplift' },
];
const GAS_BRANCHES_AR = [
    { label: 'مبيعات الغاز المحلية', sub: 'إيرادات منظمة ومستقرة' },
    { label: 'الغاز إلى طاقة CCGT', sub: 'شراء طاقة طويل الأمد' },
    { label: 'تعرفة عبور الأنابيب', sub: 'دخل من البنية التحتية' },
    { label: 'الصناعة والأسمدة', sub: 'طلب محلي مرتكز' },
    { label: 'تصدير الغاز المسال', sub: 'ميزة التسعير الدولي' },
];

const PHILOSOPHY_EN = [
    { title: 'Disciplined Capital Allocation', body: 'Every investment decision is stress-tested across multiple commodity price scenarios, phased to limit early-stage capital exposure, and structured with clear go/no-go decision gates before major commitments are made.' },
    { title: 'Phased Risk Management', body: 'Delta manages risk through disciplined phasing — committing capital in stages, validating technical and commercial assumptions at each milestone, and preserving the right to scale up or exit before irreversible capital deployment.' },
    { title: 'Integrated Value Chain Thinking', body: 'Delta evaluates every opportunity within the context of the full value chain. An upstream gas discovery is not simply a production asset — it is the foundation of a power plant, a pipeline system, a fertilizer plant, and an LNG export terminal.' },
    { title: 'Long-term Commitment', body: 'Delta is not a financial sponsor seeking a 3–5-year exit. It is a long-term industrial investor committed to building and operating energy assets across their full productive lives — measured in decades, not quarters.' },
    { title: 'Returns-focused Discipline', body: 'Project IRR 15–25%+ (upstream) · Equity IRR 18–30%+ · Payback 24–36 months · Equity Multiple 5x–20x over project life.' },
];
const PHILOSOPHY_AR = [
    { title: 'تخصيص رأس المال المنضبط', body: 'كل قرار استثماري يخضع لاختبارات إجهاد عبر سيناريوهات أسعار متعددة، ويتم تنفيذه على مراحل للحد من التعرض الرأسمالي المبكر، ويُهيكل ببوابات قرار واضحة قبل أي التزامات كبرى.' },
    { title: 'إدارة المخاطر المتدرجة', body: 'تدير دلتا المخاطر من خلال التدرج المنضبط — التزام رأس المال على مراحل، والتحقق من الافتراضات الفنية والتجارية عند كل مرحلة، والاحتفاظ بالحق في التوسع أو الخروج قبل ضخ رأس مال لا رجعة فيه.' },
    { title: 'التفكير في سلسلة القيمة المتكاملة', body: 'تقيم دلتا كل فرصة في سياق سلسلة القيمة الكاملة. الاكتشاف الغازي في القطاع العلوي ليس مجرد أصل إنتاجي — بل هو أساس محطة طاقة وشبكة أنابيب ومصنع أسمدة ومحطة تصدير غاز مسال.' },
    { title: 'الالتزام طويل الأمد', body: 'دلتا ليست راعيًا ماليًا يبحث عن خروج خلال 3–5 سنوات. بل مستثمر صناعي طويل الأمد ملتزم ببناء وتشغيل أصول الطاقة عبر كامل عمرها الإنتاجي — يُقاس بالعقود، لا بالأرباع.' },
    { title: 'الانضباط الموجه نحو العوائد', body: 'IRR للمشروع 15–25٪+ (القطاع العلوي) · IRR رأسمالي 18–30٪+ · فترة استرداد 24–36 شهرًا · مضاعف رأسمالي 5x–20x على مدى عمر المشروع.' },
];

const OUTLOOK_EN = [
    { icon: Globe2, title: 'Geopolitical Realignment', body: 'Frontier energy markets — from Afghanistan to Venezuela — are opening to investors willing to engage with complexity and sovereign risk.' },
    { icon: TrendingUp, title: 'Asian Demand Growth', body: "Urgent new markets for Central Asian and Middle Eastern gas — exactly the markets Delta's Afghanistan pipeline and LNG programs are designed to serve." },
    { icon: ShieldCheck, title: 'Energy Security Imperative', body: "Governments seeking long-term, committed energy partners — the role Delta's sovereign partnership model is designed to fulfill." },
];
const OUTLOOK_AR = [
    { icon: Globe2, title: 'إعادة المواءمة الجيوسياسية', body: 'أسواق الطاقة الناشئة — من أفغانستان إلى فنزويلا — تنفتح أمام المستثمرين المستعدين للتعامل مع التعقيد والمخاطر السيادية.' },
    { icon: TrendingUp, title: 'نمو الطلب الآسيوي', body: 'أسواق جديدة عاجلة لغاز آسيا الوسطى والشرق الأوسط — وهي بالضبط الأسواق التي صُممت برامج خطوط أنابيب دلتا في أفغانستان والغاز الطبيعي المسال لخدمتها.' },
    { icon: ShieldCheck, title: 'ضرورة أمن الطاقة', body: 'الحكومات تبحث عن شركاء طاقة ملتزمين طويلي الأمد — وهو الدور الذي صُمم نموذج الشراكة السيادية لدلتا للوفاء به.' },
];

const PILLAR_BLOCKS_EN = [
    { n: '01', icon: Compass, title: 'Frontier & Emerging Basin Exploration', quote: '"The greatest returns in oil and gas are not found where everyone is already looking. They are found where the geology is compelling, the infrastructure is nascent, and the right partner — with the right relationships — can move first."', body: "Delta's most enduring competitive advantage has always been its ability to identify high-impact basins before the mainstream investment community arrives. This early-mover philosophy has defined its most successful investments — from Central Asia in 1990, to Egypt's Nile Delta in 1999, to Uganda's Albertine Graben today." },
    { n: '02', icon: Factory, title: 'Integrated Gas Development', quote: '"Natural gas is the energy of the transition and the energy of development. Wherever we find stranded gas — in Central Asia, in Latin America, in Africa — we see the opportunity to build entire energy economies from a single resource base."', body: 'Delta has identified integrated gas development as the defining growth opportunity of the current decade. The integrated model creates multiple, compounding value streams from a single resource base — currently deployed in Afghanistan (USD 3 Bn upstream + USD 13–18 Bn midstream + 900 MW CCGT) and Venezuela (2.3 TCF across four value layers, target IRR 74.6%).' },
    { n: '03', icon: Handshake, title: 'Sovereign Partnership Model', quote: '"We do not simply invest in countries. We invest with them — aligning our capital, our expertise, and our long-term commitment with their national development objectives."', body: 'The sovereign partnership model is built on three principles: no upfront government capital required (Delta provides 100% of investment capital); long-term economic contribution (energy security, royalties, employment, technology transfer); and political durability through high-level government engagement.' },
];
const PILLAR_BLOCKS_AR = [
    { n: '01', icon: Compass, title: 'استكشاف الأحواض الحدودية والناشئة', quote: '"أعلى العوائد في النفط والغاز لا توجد حيث ينظر الجميع. بل توجد حيث الجيولوجيا مقنعة والبنية التحتية ناشئة والشريك المناسب — بالعلاقات المناسبة — يستطيع التحرك أولًا."', body: 'الميزة التنافسية الأكثر استدامة لدلتا كانت دائمًا قدرتها على تحديد الأحواض ذات التأثير العالي قبل وصول مجتمع الاستثمار الرئيسي. حددت فلسفة الدخول المبكر هذه أنجح استثماراتها — من آسيا الوسطى عام 1990، إلى دلتا النيل في مصر عام 1999، إلى وادي ألبرتاين في أوغندا اليوم.' },
    { n: '02', icon: Factory, title: 'تطوير الغاز المتكامل', quote: '"الغاز الطبيعي هو طاقة الانتقال وطاقة التنمية. أينما وجدنا غازًا غير مستغل — في آسيا الوسطى، في أمريكا اللاتينية، في إفريقيا — نرى فرصة لبناء اقتصاديات طاقة كاملة من قاعدة موارد واحدة."', body: 'حددت دلتا تطوير الغاز المتكامل كفرصة النمو المميزة للعقد الحالي. النموذج المتكامل يخلق تدفقات قيمة متعددة ومتراكمة من قاعدة موارد واحدة — يُطبق حاليًا في أفغانستان (3 مليار دولار قطاع علوي + 13–18 مليار دولار قطاع أوسط + 900 ميغاواط CCGT) وفنزويلا (2.3 تريليون قدم مكعب عبر أربع طبقات قيمة، IRR مستهدف 74.6٪).' },
    { n: '03', icon: Handshake, title: 'نموذج الشراكة السيادية', quote: '"نحن لا نستثمر في الدول فحسب. بل نستثمر معها — بمواءمة رأس مالنا وخبرتنا والتزامنا طويل الأمد مع أهدافها التنموية الوطنية."', body: 'يقوم نموذج الشراكة السيادية على ثلاثة مبادئ: لا حاجة لرأس مال حكومي مسبق (تقدم دلتا 100٪ من رأس المال الاستثماري)؛ ومساهمة اقتصادية طويلة الأمد (أمن طاقة، إتاوات، توظيف، نقل تكنولوجيا)؛ ومتانة سياسية من خلال التعاون الحكومي رفيع المستوى.' },
];

const Strategy = () => {
    const { t, lang } = useLang();
    const CRITERIA = lang === 'ar' ? CRITERIA_AR : CRITERIA_EN;
    const GAS_BRANCHES = lang === 'ar' ? GAS_BRANCHES_AR : GAS_BRANCHES_EN;
    const PHILOSOPHY = lang === 'ar' ? PHILOSOPHY_AR : PHILOSOPHY_EN;
    const OUTLOOK = lang === 'ar' ? OUTLOOK_AR : OUTLOOK_EN;
    const PILLAR_BLOCKS = lang === 'ar' ? PILLAR_BLOCKS_AR : PILLAR_BLOCKS_EN;
    return (
        <div data-testid="strategy-page">
            <PageHero
                eyebrow={t('strategy.hero_eyebrow')}
                title={t('strategy.hero_title')}
                sub={t('strategy.hero_sub')}
                image={ASSETS.yanbu}
            />

            {/* Three pillars detail */}
            <section className="bg-cream py-12">
                <div className="max-w-7xl mx-auto px-6 lg:px-10">
                    {PILLAR_BLOCKS.map((p, i) => (
                        <ScrollReveal key={p.n}>
                            <div className={`py-20 grid grid-cols-1 lg:grid-cols-12 gap-12 items-start ${i !== PILLAR_BLOCKS.length - 1 ? 'border-b border-navy/10' : ''}`}>
                                <div className="lg:col-span-4">
                                    <div className="metric-number text-5xl md:text-7xl mb-4">{p.n}</div>
                                    <div className="w-14 h-14 border border-gold/40 flex items-center justify-center mb-6">
                                        <p.icon className="w-6 h-6 text-gold" />
                                    </div>
                                    <h3 className="editorial-h2 text-navy text-2xl md:text-3xl">{p.title}</h3>
                                </div>
                                <div className="lg:col-span-8">
                                    <blockquote className="font-display italic text-navy text-lg md:text-xl leading-snug border-l-2 border-gold pl-6 mb-8">
                                        {p.quote}
                                    </blockquote>
                                    <p className="text-ink/80 leading-relaxed text-base md:text-lg">{p.body}</p>
                                </div>
                            </div>
                        </ScrollReveal>
                    ))}
                </div>
            </section>

            {/* Investment Criteria Table */}
            <section className="bg-navy-deep py-24 md:py-32">
                <div className="max-w-5xl mx-auto px-6 lg:px-10">
                    <ScrollReveal>
                        <div className="text-center max-w-2xl mx-auto mb-12">
                            <div className="eyebrow mb-5">{t('strategy.criteria_eyebrow')}</div>
                            <h2 className="editorial-h2 text-white text-3xl md:text-4xl">
                                {t('strategy.criteria_h2')}
                            </h2>
                        </div>
                    </ScrollReveal>
                    <table className="editorial-table editorial-table-dark w-full" data-testid="criteria-table">
                        <thead>
                            <tr>
                                <th>{t('strategy.col_criterion')}</th>
                                <th>{t('strategy.col_req')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {CRITERIA.map(([c, r]) => (
                                <tr key={c}>
                                    <td className="font-display text-gold">{c}</td>
                                    <td>{r}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </section>

            {/* Integrated Gas Value Chain */}
            <section className="bg-cream py-24">
                <div className="max-w-7xl mx-auto px-6 lg:px-10">
                    <div className="text-center max-w-2xl mx-auto mb-16">
                        <div className="eyebrow mb-5">{t('strategy.gas_eyebrow')}</div>
                        <h2 className="editorial-h2 text-navy text-3xl md:text-4xl">
                            {t('strategy.gas_h2')}
                        </h2>
                    </div>

                    <div className="flex flex-col items-center">
                        <div className="bg-gold text-navy-deep px-10 py-6 inline-flex flex-col items-center mb-2">
                            <div className="text-[10px] tracking-widest uppercase">Source</div>
                            <div className="font-display text-2xl md:text-3xl">{t('strategy.gas_source')}</div>
                        </div>
                        <div className="w-px h-12 bg-gold" />

                        <div className="grid grid-cols-1 md:grid-cols-5 gap-px bg-navy/10 w-full">
                            {GAS_BRANCHES.map((b, i) => (
                                <ScrollReveal key={b.label} delay={i * 0.05} className="bg-cream">
                                    <div className="relative border-t border-gold/40 p-6 hover:bg-white transition-colors h-full">
                                        <div className="metric-number text-xl mb-2">{String(i + 1).padStart(2, '0')}</div>
                                        <div className="font-display text-navy text-base md:text-lg leading-snug mb-2">{b.label}</div>
                                        <div className="text-ink/60 text-xs">{b.sub}</div>
                                    </div>
                                </ScrollReveal>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Philosophy */}
            <section className="bg-white py-24 md:py-32">
                <div className="max-w-7xl mx-auto px-6 lg:px-10">
                    <div className="text-center max-w-2xl mx-auto mb-16">
                        <div className="eyebrow mb-5">{t('strategy.philosophy_eyebrow')}</div>
                        <h2 className="editorial-h2 text-navy text-3xl md:text-4xl">
                            {t('strategy.philosophy_h2')}
                        </h2>
                    </div>
                    <Stagger className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-px bg-navy/10">
                        {PHILOSOPHY.map((p, i) => (
                            <StaggerItem key={p.title} testid={`philosophy-${i}`} className="bg-white p-7 hover:bg-cream transition-colors">
                                <div className="metric-number text-2xl md:text-3xl mb-4">{String(i + 1).padStart(2, '0')}</div>
                                <h3 className="font-display text-navy text-lg leading-snug mb-3">{p.title}</h3>
                                <p className="text-ink/70 text-sm leading-relaxed">{p.body}</p>
                            </StaggerItem>
                        ))}
                    </Stagger>
                </div>
            </section>

            {/* Outlook */}
            <section className="bg-navy-deep py-24 md:py-32 grain-overlay">
                <div className="max-w-7xl mx-auto px-6 lg:px-10">
                    <div className="text-center max-w-2xl mx-auto mb-16">
                        <div className="eyebrow mb-5">{t('strategy.outlook_eyebrow')}</div>
                        <h2 className="editorial-h2 text-white text-3xl md:text-4xl">
                            {t('strategy.outlook_h2')}
                        </h2>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-gold/15">
                        {OUTLOOK.map((o, i) => (
                            <ScrollReveal key={o.title} delay={i * 0.1} className="bg-navy-deep p-10 hover:bg-navy-mid transition-colors">
                                <div className="w-12 h-12 border border-gold/40 flex items-center justify-center mb-6">
                                    <o.icon className="w-5 h-5 text-gold" />
                                </div>
                                <h3 className="font-display text-white text-xl mb-3">{o.title}</h3>
                                <p className="text-white/65 text-sm leading-relaxed">{o.body}</p>
                            </ScrollReveal>
                        ))}
                    </div>
                </div>
            </section>
        </div>
    );
};

export default Strategy;
